/**
 * MyGradesPage — Phase 1.7f
 * Read-only, student-facing view of THEIR composite course % and
 * per-category breakdown across Assignment / Discussion / Quiz /
 * Presentation / Exam. Lists every finalized submission with a deep
 * link to the grader page (where AI feedback + citations live).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { getMyGrades } from '../lib/apiClient';

const CATEGORIES = ['assignment', 'discussion', 'quiz', 'presentation', 'exam', 'learnex'];
const CAT_LABEL = {
  assignment:   'Assignment',
  discussion:   'Discussion',
  quiz:         'Quiz',
  presentation: 'Presentation',
  exam:         'Exam',
  learnex:      'LearnEx',
  other:        'Other',
};
const CAT_EMOJI = {
  assignment:   '📝',
  discussion:   '💬',
  quiz:         '❓',
  presentation: '🎞',
  exam:         '🎓',
  learnex:      '🧠',
  other:        '·',
};

const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch (_) { return iso; }
};

const grade = (pct) => {
  if (pct == null) return { letter: '—', tint: 'text-gray-400' };
  if (pct >= 90) return { letter: 'A', tint: 'text-emerald-700' };
  if (pct >= 80) return { letter: 'B', tint: 'text-emerald-600' };
  if (pct >= 70) return { letter: 'C', tint: 'text-amber-600' };
  if (pct >= 60) return { letter: 'D', tint: 'text-amber-700' };
  return { letter: 'F', tint: 'text-red-600' };
};

const CategoryCard = ({ cat, info }) => {
  const has = info && info.count > 0;
  const g = grade(info?.avg_pct);
  return (
    <div className="card p-4" data-testid={`my-cat-${cat}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          {CAT_EMOJI[cat]} {CAT_LABEL[cat]}
        </div>
        <span className="text-[10px] text-gray-500">weight {info?.weight ?? 0}%</span>
      </div>
      {has ? (
        <>
          <div className={`text-2xl font-bold ${g.tint}`}>{fmtPct(info.avg_pct)}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {info.count} submission{info.count === 1 ? '' : 's'} · grade <strong className={g.tint}>{g.letter}</strong>
          </div>
        </>
      ) : (
        <>
          <div className="text-2xl text-gray-300 font-light">—</div>
          <div className="text-xs text-gray-500 mt-0.5">No submissions yet</div>
        </>
      )}
    </div>
  );
};

const MyGradesPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    getMyGrades()
      .then(setData)
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load grades'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen"><Navbar /><div className="p-8 text-gray-500">Loading your grades…</div></div>
  );
  if (err) return (
    <div className="min-h-screen"><Navbar /><div className="p-8 text-red-700">{err}</div></div>
  );
  if (!data) return null;

  const composite = data.composite_pct;
  const curved = data.composite_curved;
  const compG = grade(composite);
  const curveG = grade(curved);

  // Group submissions by category for the lists
  const subsByCat = CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: [] }), { other: [] });
  for (const s of (data.submissions || [])) {
    const key = subsByCat[s.category] ? s.category : 'other';
    subsByCat[key].push(s);
  }

  return (
    <div className="min-h-screen" data-testid="my-grades-page">
      <Navbar subtitle="My Grades" />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700">
              Course progress · read-only
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">🎓 My Grades</h1>
            <p className="text-gray-500 mt-1">
              Your composite course %, weighted across all evaluation categories. Curved totals (if applied by faculty) appear alongside.
            </p>
          </div>
          <Link to="/rubrics" className="btn-outline text-sm" data-testid="my-grades-rubrics-link">
            📝 Graded Work
          </Link>
        </div>

        {/* Composite hero card */}
        <section className="card p-6 mb-6" data-testid="composite-hero">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-1">
                Composite course grade
              </div>
              <div className="flex items-baseline gap-3">
                <div className={`text-6xl font-bold ${compG.tint}`} data-testid="composite-value">
                  {fmtPct(composite)}
                </div>
                <div className={`text-3xl font-bold ${compG.tint}`}>{compG.letter}</div>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                {data.submission_count} finalized submission{data.submission_count === 1 ? '' : 's'} ·
                {' '}weight coverage {data.weight_coverage_pct}% ·
                {' '}{data.composite_pct == null
                      ? 'no weighted submissions yet'
                      : `categories with submissions count toward your grade`}
              </div>
            </div>
            {curved != null && (
              <div data-testid="curved-block" className="border-l border-gray-200 pl-6">
                <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-1">
                  After course curve
                </div>
                <div className="flex items-baseline gap-3">
                  <div className={`text-5xl font-bold ${curveG.tint}`} data-testid="curved-value">
                    {fmtPct(curved)}
                  </div>
                  <div className={`text-2xl font-bold ${curveG.tint}`}>{curveG.letter}</div>
                </div>
                {data.course_curve_params && (
                  <div className="text-xs text-gray-500 mt-2">
                    Curve: {data.course_curve_params.mode}
                    {data.course_curve_params.mode === 'bump' && data.course_curve_params.bump_points
                      ? ` +${data.course_curve_params.bump_points}%`
                      : ''}
                    {data.course_curve_params.mode === 'scale' && data.course_curve_params.scale_factor
                      ? ` ×${Number(data.course_curve_params.scale_factor).toFixed(2)}`
                      : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Per-category cards */}
        <section className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8" data-testid="category-cards">
          {CATEGORIES.map((c) => (
            <CategoryCard key={c} cat={c} info={data.per_category?.[c]} />
          ))}
        </section>

        {/* Submissions per category */}
        <section data-testid="submissions-section">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Submissions</h2>
          {data.submission_count === 0 ? (
            <div className="card p-8 text-center text-gray-500" data-testid="no-submissions">
              You haven&apos;t been graded on anything yet. Head to <Link to="/rubrics" className="text-emerald-700 underline">Graded Work</Link> to submit.
            </div>
          ) : (
            <div className="space-y-5">
              {CATEGORIES.concat('other').map((cat) => {
                const items = subsByCat[cat] || [];
                if (items.length === 0) return null;
                return (
                  <div key={cat} data-testid={`subs-group-${cat}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                      {CAT_EMOJI[cat]} {CAT_LABEL[cat]} ({items.length})
                    </div>
                    <div className="space-y-1.5">
                      {items.map((s) => {
                        const g = grade(s.pct);
                        const linkTo = s.category === 'learnex' && s.module_id && s.topic_id
                          ? `/modules/${s.module_id}?topic=${s.topic_id}&tab=learnex`
                          : `/submissions/${s.submission_id}`;
                        return (
                          <Link
                            key={s.submission_id}
                            to={linkTo}
                            className="card p-3 flex items-center justify-between hover:shadow"
                            data-testid={`my-sub-${s.submission_id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-gray-900 truncate">{s.rubric_title}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                                {s.category === 'learnex'
                                  ? <>AI-judged quiz pass · {s.final_total}/{s.max_total} correct · {fmtDate(s.finalized_at)}</>
                                  : <>{s.criterion_count} criteria · finalized {fmtDate(s.finalized_at)}{s.module_id ? <> · {s.module_id}</> : null}</>
                                }
                              </div>
                            </div>
                            <div className="flex items-baseline gap-3 shrink-0">
                              <span className="font-mono text-sm text-gray-600">
                                {s.final_total ?? '—'}/{s.max_total ?? '—'}
                              </span>
                              <span className={`font-mono text-lg font-bold ${g.tint}`}>
                                {fmtPct(s.pct)}
                              </span>
                              <span className={`font-bold text-lg ${g.tint}`}>{g.letter}</span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* G4.1 · Portable Skill Passport */}
        <SkillPassport />

        <div className="text-xs text-gray-400 mt-8 italic">
          Tip: click any submission row above to see Claude&apos;s per-criterion feedback and the course-material citations behind each score.
        </div>
      </main>
    </div>
  );
};

export default MyGradesPage;

/**
 * G4.1 — Portable Skill Passport
 * Reads `/api/gradebook/me/skill-passport` and renders one row per rubric
 * (the student's strongest submission) with the specific criteria they
 * demonstrated ≥ 70% on. Downloadable JSON for offline / job-market use.
 */
function SkillPassport() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token') || '';
    fetch(`${API_URL}/api/gradebook/me/skill-passport`, {
      headers: { 'Authorization': token ? `Bearer ${token}` : '' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setErr(e.message || 'Failed to load skill passport'));
  }, []);

  if (err) {
    return (
      <section className="card p-6 mb-6" data-testid="skill-passport-error">
        <div className="text-sm text-red-700">{err}</div>
      </section>
    );
  }
  if (!data) return null;
  if (!data.skills || data.skills.length === 0) {
    return (
      <section className="card p-6 mb-6" data-testid="skill-passport-empty">
        <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-1">
          🎫 Skill Passport
        </div>
        <div className="text-sm text-gray-500">
          Skills you demonstrate on graded work will appear here — one row per rubric, with the specific criteria you scored ≥ 70% on. Complete a rubric to seed it.
        </div>
      </section>
    );
  }

  const download = () => {
    const blob = new Blob(
      [JSON.stringify({
        student_name: data.student_name,
        generated_at: data.generated_at,
        skills: data.skills.map((s) => ({
          skill: s.rubric_title,
          category: s.category,
          best_pct: s.best_pct,
          demonstrated_criteria: s.demonstrated_criteria,
        })),
      }, null, 2)],
      { type: 'application/json' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skill-passport-${(data.student_name || 'student').replace(/\s+/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <section
      className="card p-6 mb-6"
      data-testid="skill-passport"
      style={{
        borderTop: '3px solid #16A34A',
        background: 'linear-gradient(135deg, rgba(22,163,74,0.04) 0%, white 60%)',
      }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-1">
            🎫 Skill Passport
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            {data.skill_count} skill{data.skill_count === 1 ? '' : 's'} demonstrated
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Each row is your strongest submission per rubric with the criteria you nailed ≥ 70%. Carry it to interviews or portfolio reviews.
          </p>
        </div>
        <button
          onClick={download}
          className="btn-primary text-sm"
          data-testid="skill-passport-download"
        >
          ⬇ Download JSON
        </button>
      </div>

      <div className="space-y-2">
        {data.skills.map((s) => {
          const g = grade(s.best_pct);
          return (
            <div
              key={s.rubric_id}
              className="rounded border p-3 flex items-center justify-between gap-3 flex-wrap bg-white"
              data-testid={`skill-row-${s.rubric_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 truncate">
                  {CAT_EMOJI[s.category] || '·'} {s.rubric_title}
                </div>
                {s.demonstrated_criteria && s.demonstrated_criteria.length > 0 ? (
                  <div className="text-xs text-gray-600 mt-1">
                    Demonstrated: {s.demonstrated_criteria.slice(0, 6).map((c) => (
                      <span
                        key={c}
                        className="inline-block bg-emerald-50 text-emerald-800 rounded px-2 py-0.5 mr-1 mb-1 border border-emerald-200"
                      >{c}</span>
                    ))}
                    {s.demonstrated_criteria.length > 6 && (
                      <span className="text-gray-500">+{s.demonstrated_criteria.length - 6} more</span>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 mt-1 italic">
                    No criteria scored ≥ 70% on this rubric yet.
                  </div>
                )}
              </div>
              <div className="flex items-baseline gap-3 shrink-0">
                <span className="font-mono text-sm text-gray-600">
                  {s.best_raw}/{s.max_total}
                </span>
                <span className={`font-mono text-lg font-bold ${g.tint}`}>{fmtPct(s.best_pct)}</span>
                <span className={`font-bold text-lg ${g.tint}`}>{g.letter}</span>
                <Link
                  to={`/submissions/${s.best_submission_id}`}
                  className="text-xs text-emerald-700 underline"
                >view</Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
